import { useCallback } from "react";
import { useSnackbar } from "notistack";
import { Button } from "@mui/material";
import { api, backendPost } from "@accredo/fe-shared/services/dataService.js";

/**
 * Marks imported payments as "nicht relevant" (soft delete) and restores them.
 * Every ignore action offers "Rückgängig" in a snackbar.
 *
 * `onDone` runs after the BE call succeeded so callers can reload their lists.
 */
export function usePaymentIgnoreActions() {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const restorePayments = useCallback(
    async (paymentIds, { onDone } = {}) => {
      const response = await backendPost("data/payments/restore", {
        label: "Zahlungen wiederherstellen",
        toSend: { payment_ids: paymentIds },
        enqueueSnackbar,
      });
      if (!response) return null;

      const skipped = response.skipped_ids?.length ?? 0;
      if (skipped > 0) {
        enqueueSnackbar(
          `${response.restored_ids.length} wiederhergestellt, ${skipped} übersprungen (bereits aktiv vorhanden)`,
          { variant: "warning" }
        );
      }
      onDone?.(response);
      return response;
    },
    [enqueueSnackbar]
  );

  const showUndoSnackbar = useCallback(
    (message, paymentIds, onDone) => {
      enqueueSnackbar(message, {
        variant: "success",
        action: (key) => (
          <Button
            color="inherit"
            size="small"
            onClick={async () => {
              closeSnackbar(key);
              await restorePayments(paymentIds, { onDone });
            }}
          >
            Rückgängig
          </Button>
        ),
      });
    },
    [enqueueSnackbar, closeSnackbar, restorePayments]
  );

  const ignorePayment = useCallback(
    async (paymentId, { onDone } = {}) => {
      try {
        await api.delete(`data/payments/${paymentId}`);
      } catch (error) {
        enqueueSnackbar(
          error?.response?.data?.message ||
            "Zahlung konnte nicht als nicht relevant markiert werden.",
          { variant: "error" }
        );
        return false;
      }
      showUndoSnackbar(
        "Zahlung als nicht relevant markiert",
        [paymentId],
        onDone
      );
      onDone?.();
      return true;
    },
    [enqueueSnackbar, showUndoSnackbar]
  );

  const ignoreRemainingPayments = useCallback(
    async (paymentImportId, { onDone } = {}) => {
      const response = await backendPost(
        `data/payment-imports/${paymentImportId}/ignore-remaining`,
        {
          label: "Zahlungen als nicht relevant markieren",
          enqueueSnackbar,
        }
      );
      if (!response) return false;

      const ids = response.ignored_ids ?? [];
      showUndoSnackbar(
        `${ids.length} Zahlungen als nicht relevant markiert`,
        ids,
        onDone
      );
      onDone?.();
      return true;
    },
    [enqueueSnackbar, showUndoSnackbar]
  );

  return { ignorePayment, ignoreRemainingPayments, restorePayments };
}
